import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../Components/Layout/Layout';
import ReCAPTCHA from "react-google-recaptcha";
import { Input } from "@/Components/ui/input";
import { Button } from "@/Components/ui/button"; 
import axiosInstance from '../utils/axiosConfig';
import { CheckCircle2 } from "lucide-react";

const Helpdesk = () => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        subject: '',
        email: '',
        question: ''
    });
    const [captchaValue, setCaptchaValue] = useState(null);
    const [error, setError] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [success, setSuccess] = useState('');

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault(); 
        if (!captchaValue) {
            setError(t('helpdesk.fillCaptcha'));
            return;
        }

        setIsSending(true);
        setError('');

        try {
            const response = await axiosInstance.post('/helpdesk/submit', {
                ...formData,
                recaptcha: captchaValue
            });
             
            if (response.status !== 200) {
                setError(response.message || t('helpdesk.error'));
            } else {
                setShowSuccess(true);
                setSuccess(t('helpdesk.success'));

                setFormData({
                    subject: '',
                    email: '',
                    question: ''
                });
                setCaptchaValue(null); 

                setTimeout(() => {
                    setShowSuccess(false); 
                }, 2000);
            } 
        } catch (error) {
            setError(t('helpdesk.error'));
        } finally {
            setIsSending(false);
            window.scrollTo({ top: 0, behavior: 'smooth' }); 
        }
    };

    return (
        <Layout>
            <div className="relative max-w-[1100px] mx-auto">

                {showSuccess && (
                    <div className="mb-4 p-4 bg-green-50 text-green-700 rounded-lg flex items-center gap-2">
                        <CheckCircle2 className="h-5 w-5" />
                        <span className="font-inter">{success}</span>
                    </div>
                )}

                <h1 className="text-2xl font-bold text-ps-blue-dark mb-8">{t('helpdesk.title')}</h1>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t('helpdesk.subject')}
                        </label>
                        <Input
                            type="text"
                            name="subject"
                            value={formData.subject}
                            onChange={handleChange}
                            required
                            className="w-full"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t('helpdesk.emailAddress')}
                        </label>
                        <Input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className="w-full"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t('helpdesk.question')}
                        </label>
                        <textarea
                            name="question"
                            value={formData.question}
                            onChange={handleChange}
                            required
                            rows={5}
                            className="w-full p-2 border rounded-md"
                        />
                    </div>

                    {error && (
                        <p className="text-red-500 text-sm">{error}</p>
                    )}
                    
                    <div className="flex justify-end">
                        <Button
                            type="submit"
                            className="bg-ps-blue-dark hover:bg-ps-blue-darker text-white w-60"
                            disabled={!captchaValue}
                        >
                            {isSending ? t('helpdesk.sending') : t('helpdesk.send')}
                        </Button>
                    </div>

                    <div className="flex justify-center">
                        <ReCAPTCHA
                            sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY} 
                            onChange={(value) => setCaptchaValue(value)}
                        />
                    </div>
                </form>
            </div>
        </Layout>
    );
};

export default Helpdesk;